import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Button, Form, Ref } from 'semantic-ui-react'
import * as React from 'react'
import {
  BtnClearFormatting,
  BtnStrikeThrough,
  BtnNumberedList,
  EditorProvider,
  BtnBulletList,
  BtnUnderline,
  HtmlButton,
  Separator,
  BtnItalic,
  Toolbar,
  BtnLink,
  BtnRedo,
  BtnBold,
  BtnUndo,
  Editor,
} from 'react-simple-wysiwyg'
import { AxiosError } from 'axios'

import NoteTemplatesDropdown from '../../NoteTemplatesDropdown'
import SaveAsTemplateBtn from '../../SaveAsTemplateBtn'
import { classNames } from '../../../utils'
import { Collection } from '../../../types'
import { useToasts } from '../../toasts/ToastsProvider'
import { useApi } from '../../../store/mainContext'

function getStorageKey(relatedId: string) {
  return 'event-editor.' + relatedId
}

type Category =
  | 'Customer Interview'
  | 'Candidate Feedback'
  | 'Customer Feedback'
  | 'Screening Notes'
  | 'Email or Chat'
  | 'Meeting'
  | 'Note'

type FormValues = { category: Category; body: string }

interface Props {
  relatedCollection: Collection
  relatedId: string
  onSuccess(): Promise<any>
  onCancel?(): void
  formId?: string
}

export default function NoteForm(props: Props) {
  const {
    relatedCollection,
    onSuccess,
    relatedId,
    onCancel,
    formId = 'note-form',
  } = props

  const { addToast } = useToasts()
  const api = useApi()

  const [isSaving, setIsSaving] = React.useState(false)

  const form = useForm<FormValues>({
    defaultValues: {
      category: 'Note',
      body: window.localStorage.getItem(getStorageKey(relatedId)) || '',
    },
  })

  const cleanupForm = () => {
    window.localStorage.removeItem(getStorageKey(relatedId))
    form.reset({ body: '' })
  }

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsSaving(true)
    return api
      .post('events', {
        description: data.body,
        title: data.category,
        type: 'note',
        relatedId,
        relatedCollection,
      })
      .then(() => onSuccess())
      .then(cleanupForm)
      .catch((err: AxiosError) => {
        addToast(err.response?.data.message || err.message, {
          variant: 'danger',
        })
      })
      .finally(() => setIsSaving(false))
  }

  return (
    <div>
      <Form
        className="relative z-10"
        onSubmit={form.handleSubmit(onSubmit)}
        spellCheck="false"
        id={formId}
      >
        <Controller
          name="category"
          control={form.control}
          rules={{ required: 'A category is required' }}
          render={({ field: { value, onChange } }) => {
            return (
              <Button.Group
                className="!mb-3 !mt-3 w-full overflow-x-auto rounded bg-white [&::-webkit-scrollbar]:hidden"
                size="tiny"
                basic
              >
                <Button
                  active={value === 'Note'}
                  onClick={() => onChange('Note')}
                  disabled={isSaving}
                  type="button"
                >
                  Random <br />
                  Note
                </Button>
                <Button
                  active={value === 'Screening Notes'}
                  onClick={() => onChange('Screening Notes')}
                  disabled={isSaving}
                  type="button"
                >
                  Screening
                  <br />
                  Notes
                </Button>
                <Button
                  active={value === 'Customer Interview'}
                  onClick={() => onChange('Customer Interview')}
                  disabled={isSaving}
                  type="button"
                >
                  Customer
                  <br />
                  Interview
                </Button>
                <Button
                  active={value === 'Customer Feedback'}
                  onClick={() => onChange('Customer Feedback')}
                  disabled={isSaving}
                  type="button"
                >
                  Customer
                  <br />
                  Feedback
                </Button>
                <Button
                  active={value === 'Candidate Feedback'}
                  onClick={() => onChange('Candidate Feedback')}
                  disabled={isSaving}
                  type="button"
                >
                  Candidate
                  <br />
                  Feedback
                </Button>
                <Button
                  active={value === 'Email or Chat'}
                  onClick={() => onChange('Email or Chat')}
                  disabled={isSaving}
                  type="button"
                >
                  Email or
                  <br /> Chat
                </Button>
                <Button
                  active={value === 'Meeting'}
                  onClick={() => onChange('Meeting')}
                  disabled={isSaving}
                  type="button"
                >
                  Meeting
                </Button>
              </Button.Group>
            )
          }}
        />
        <Controller
          name="body"
          control={form.control}
          rules={{ required: 'A message is required' }}
          render={({ field }) => {
            const isDisabled = form.formState.isSubmitting
            const { ref, ...other } = field
            return (
              <div
                className={classNames(
                  'relative',
                  form.formState.errors.body && 'rsw-has-error',
                  isDisabled && 'rsw-is-disabled',
                )}
              >
                <EditorProvider>
                  <Ref innerRef={ref}>
                    <Editor
                      {...other}
                      onChange={(e) => {
                        window.localStorage.setItem(
                          getStorageKey(relatedId),
                          e.target.value,
                        )
                        other.onChange(e)
                      }}
                      disabled={isDisabled}
                      // @ts-ignore
                      placeholder="Say something smart..."
                    >
                      <Toolbar>
                        <BtnUndo />
                        <BtnRedo />
                        <Separator />
                        <BtnBold />
                        <BtnItalic />
                        <BtnUnderline />
                        <BtnStrikeThrough />
                        <Separator />
                        <BtnNumberedList />
                        <BtnBulletList />
                        <Separator />
                        <BtnLink />
                        <BtnClearFormatting />
                        <HtmlButton />
                      </Toolbar>
                    </Editor>
                  </Ref>
                </EditorProvider>
                <div className="absolute right-1.5 top-[5px]">
                  <NoteTemplatesDropdown
                    disabled={isDisabled}
                    onChange={other.onChange}
                  />
                </div>
                <div className="mt-1 text-base leading-snug text-red-500">
                  {[
                    form.formState.errors.category?.message,
                    form.formState.errors.body?.message,
                  ]
                    .filter(Boolean)
                    .join('. ')}
                </div>
              </div>
            )
          }}
        />
      </Form>
      <div className="mt-6 text-right">
        {' '}
        <Button
          basic
          disabled={isSaving}
          onClick={() => {
            cleanupForm()
            onCancel?.()
          }}
          type="button"
          content="Cancel"
        />
        <SaveAsTemplateBtn
          disabled={isSaving}
          text={form.watch('body')}
          askForName
        />
        <Button
          type="submit"
          form={formId}
          content="Save"
          icon="save"
          primary
          loading={isSaving}
          disabled={isSaving}
        />
      </div>
    </div>
  )
}
